import { createSlice } from '@reduxjs/toolkit'

// Initial state
const initialState = {
  // Booking state
  selectedDoctor: null,
  showAvailabilityView: false,
  selectedSlot: null,
  selectedReason: null,
  availabilityData: null,
  currentView: 'calendar', // 'calendar' or 'confirmation'

  // Availability calendar state
  currentWeekStart: new Date(),
  hasInitializedWeek: false,

  // Booking flow state
  bookingLoading: false,
  bookingError: null,
  bookingSuccess: false,
  bookingHistory: [],

  // UI state
  showReasonModal: false,
  agreedToTerms: false
}

// Create slice
const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    // Basic setters
    setSelectedDoctor: (state, action) => {
      state.selectedDoctor = action.payload
    },

    setShowAvailabilityView: (state, action) => {
      state.showAvailabilityView = action.payload
    },

    setSelectedSlot: (state, action) => {
      state.selectedSlot = action.payload
    },

    setSelectedReason: (state, action) => {
      state.selectedReason = action.payload
    },

    setAvailabilityData: (state, action) => {
      state.availabilityData = action.payload
    },

    setCurrentView: (state, action) => {
      state.currentView = action.payload
    },

    setCurrentWeekStart: (state, action) => {
      state.currentWeekStart = action.payload
    },

    setHasInitializedWeek: (state, action) => {
      state.hasInitializedWeek = action.payload
    },

    setShowReasonModal: (state, action) => {
      state.showReasonModal = action.payload
    },

    setAgreedToTerms: (state, action) => {
      state.agreedToTerms = action.payload
    },

    setBookingLoading: (state, action) => {
      state.bookingLoading = action.payload
    },

    setBookingError: (state, action) => {
      state.bookingError = action.payload
    },

    setBookingSuccess: (state, action) => {
      state.bookingSuccess = action.payload
    },

    clearBookingError: (state) => {
      state.bookingError = null
    },

    // Complex actions
    openAvailabilityView: (state, action) => {
      state.selectedDoctor = action.payload
      state.showAvailabilityView = true
      state.currentView = 'calendar'
      state.selectedSlot = null
      state.selectedReason = null
      state.bookingError = null
      state.bookingSuccess = false
      state.hasInitializedWeek = false
    },

    closeAvailabilityView: (state) => {
      state.showAvailabilityView = false
      state.selectedDoctor = null
      state.selectedSlot = null
      state.selectedReason = null
      state.availabilityData = null
      state.currentView = 'calendar'
      state.bookingError = null
      state.bookingSuccess = false
      state.hasInitializedWeek = false
      state.agreedToTerms = false
    },

    navigateWeek: (state, action) => {
      const direction = action.payload
      const newDate = new Date(state.currentWeekStart)
      newDate.setDate(newDate.getDate() + direction * 7)

      // Prevent going back to past weeks
      if (direction < 0) {
        const today = new Date()
        const getMonday = (date) => {
          const d = new Date(date)
          const day = d.getDay()
          const diff = d.getDate() - day + (day === 0 ? -6 : 1)
          return new Date(d.setDate(diff))
        }
        const currentWeekMonday = getMonday(today)

        if (newDate < currentWeekMonday) {
          return // Block navigation to past weeks
        }
      }

      state.currentWeekStart = newDate
    },

    selectSlot: (state, action) => {
      const slot = action.payload

      if (!state.selectedReason) {
        state.showReasonModal = true
        return
      }

      state.selectedSlot = slot
      state.currentView = 'confirmation'
    },

    backToCalendar: (state) => {
      state.currentView = 'calendar'
      state.selectedSlot = null
      state.agreedToTerms = false
      state.bookingError = null
    },

    resetBookingState: (state) => {
      state.selectedSlot = null
      state.selectedReason = null
      state.currentView = 'calendar'
      state.bookingError = null
      state.bookingSuccess = false
      state.showReasonModal = false
      state.agreedToTerms = false
    },

    // Availability async actions
    fetchAvailabilityStart: (state) => {
      state.bookingLoading = true
      state.bookingError = null
    },

    fetchAvailabilitySuccess: (state, action) => {
      state.bookingLoading = false
      state.availabilityData = action.payload.availability

      // Auto-navigate to the week containing the earliest available appointment
      if (!state.hasInitializedWeek && action.payload.availability?.availability?.length > 0) {
        const earliestDate = action.payload.availability.availability
          .filter((dayAvail) => dayAvail.timeSlots && dayAvail.timeSlots.length > 0)
          .map((dayAvail) => new Date(dayAvail.date))
          .sort((a, b) => a - b)[0]

        if (earliestDate) {
          const getMonday = (date) => {
            const d = new Date(date)
            const day = d.getDay()
            const diff = d.getDate() - day + (day === 0 ? -6 : 1)
            return new Date(d.setDate(diff))
          }
          state.currentWeekStart = getMonday(earliestDate)
          state.hasInitializedWeek = true
        }
      }
    },

    fetchAvailabilityFailure: (state, action) => {
      state.bookingLoading = false
      state.availabilityData = null
      state.bookingError = action.payload
    },

    // Booking async actions
    confirmBookingStart: (state) => {
      state.bookingLoading = true
      state.bookingError = null
    },

    confirmBookingSuccess: (state, action) => {
      state.bookingLoading = false
      state.bookingSuccess = true

      // Add to booking history
      const newBooking = {
        id: action.payload.id || Date.now(),
        doctor: state.selectedDoctor,
        slot: state.selectedSlot,
        reason: state.selectedReason,
        bookingDate: new Date().toISOString(),
        status: 'confirmed'
      }

      state.bookingHistory = [newBooking, ...state.bookingHistory.slice(0, 9)]
    },

    confirmBookingFailure: (state, action) => {
      state.bookingLoading = false
      state.bookingError = action.payload
    },

    // Booking history management
    addToBookingHistory: (state, action) => {
      state.bookingHistory = [action.payload, ...state.bookingHistory].slice(0, 20)
    },

    clearBookingHistory: (state) => {
      state.bookingHistory = []
    },

    // Reset entire state
    resetBooking: (state) => {
      return { ...initialState, bookingHistory: state.bookingHistory }
    }
  }
})

// Export actions
export const {
  setSelectedDoctor,
  setShowAvailabilityView,
  setSelectedSlot,
  setSelectedReason,
  setAvailabilityData,
  setCurrentView,
  setCurrentWeekStart,
  setHasInitializedWeek,
  setShowReasonModal,
  setAgreedToTerms,
  setBookingLoading,
  setBookingError,
  setBookingSuccess,
  clearBookingError,
  openAvailabilityView,
  closeAvailabilityView,
  navigateWeek,
  selectSlot,
  backToCalendar,
  resetBookingState,
  fetchAvailabilityStart,
  fetchAvailabilitySuccess,
  fetchAvailabilityFailure,
  confirmBookingStart,
  confirmBookingSuccess,
  confirmBookingFailure,
  addToBookingHistory,
  clearBookingHistory,
  resetBooking
} = bookingSlice.actions

// Export reducer
export default bookingSlice

// Selectors
export const selectBookingState = (state) => state.searchEngineBooking
export const selectSelectedDoctor = (state) => state.searchEngineBooking.selectedDoctor
export const selectShowAvailabilityView = (state) => state.searchEngineBooking.showAvailabilityView
export const selectSelectedSlot = (state) => state.searchEngineBooking.selectedSlot
export const selectAvailabilityData = (state) => state.searchEngineBooking.availabilityData
export const selectCurrentView = (state) => state.searchEngineBooking.currentView
export const selectBookingLoading = (state) => state.searchEngineBooking.bookingLoading
export const selectBookingError = (state) => state.searchEngineBooking.bookingError
export const selectBookingSuccess = (state) => state.searchEngineBooking.bookingSuccess
export const selectBookingHistory = (state) => state.searchEngineBooking.bookingHistory
